import type { GfView } from "../../../../types/gifting";
import { SAVED_FILTERS, type Density, type GroupBy, type SavedFilterKey } from "./listModel";

function Seg<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="gf-tb-seg" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          className={`gf-tb-seg-btn${o.value === value ? " is-on" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const GROUP_OPTIONS: ReadonlyArray<{ value: GroupBy; label: string }> = [
  { value: "none", label: "なし" },
  { value: "camp", label: "案件" },
  { value: "stage", label: "段階" },
  { value: "act", label: "アクション" },
];
const DENSITY_OPTIONS: ReadonlyArray<{ value: Density; label: string }> = [
  { value: "normal", label: "通常" },
  { value: "compact", label: "コンパクト" },
];

/** ツールバー（README ①c）: 表示切替・検索・案件チップ・保存フィルタ・グループ・密度・件数 */
export function ListToolbar({
  view,
  onView,
  archivedCount,
  q,
  onQ,
  campaignChips,
  campaign,
  onCampaign,
  saved,
  onSaved,
  group,
  onGroup,
  density,
  onDensity,
  shown,
  pool,
}: {
  view: GfView;
  onView: (v: GfView) => void;
  archivedCount: number;
  q: string;
  onQ: (q: string) => void;
  campaignChips: Array<{ id: string; name: string; count: number }>;
  campaign: string;
  onCampaign: (id: string) => void;
  saved: SavedFilterKey | null;
  onSaved: (k: SavedFilterKey | null) => void;
  group: GroupBy;
  onGroup: (g: GroupBy) => void;
  density: Density;
  onDensity: (d: Density) => void;
  shown: number;
  pool: number;
}) {
  const archive = view === "archive";
  const list = view === "list";
  return (
    <div className="gf-tb">
      <Seg<GfView>
        label="表示切替"
        value={view}
        onChange={onView}
        options={[
          { value: "list", label: "一覧" },
          { value: "kanban", label: "カンバン（補助）" },
          { value: "archive", label: `アーカイブ (${archivedCount})` },
        ]}
      />
      {!archive && (
        <>
          <input
            className="gf-tb-search"
            type="search"
            value={q}
            onChange={(e) => onQ(e.target.value)}
            placeholder="名前・@ハンドル・案件で検索"
            aria-label="検索"
          />
          {campaignChips.length > 1 && (
            <div className="gf-tb-pills" role="group" aria-label="案件で絞り込み">
              <span className="gf-tb-label">案件</span>
              {campaignChips.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  className={`gf-tb-pill${campaign === c.id ? " is-on" : ""}`}
                  aria-pressed={campaign === c.id}
                  onClick={() => onCampaign(c.id)}
                  title={`${c.count}名`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
          <div className="gf-tb-pills" role="group" aria-label="保存フィルタ">
            <span className="gf-tb-label">保存フィルタ</span>
            {SAVED_FILTERS.map((f) => (
              <button
                type="button"
                key={f.key}
                className={`gf-tb-pill${saved === f.key ? " is-on" : ""}`}
                aria-pressed={saved === f.key}
                onClick={() => onSaved(saved === f.key ? null : f.key)}
              >
                {f.label}
              </button>
            ))}
            <button type="button" className="gf-tb-pill is-add" disabled title="フィルタの保存は準備中です">
              ＋ 保存
            </button>
          </div>
        </>
      )}
      <div className="gf-tb-right">
        {list && (
          <>
            <span className="gf-tb-label">グループ</span>
            <Seg<GroupBy> label="グループ" value={group} onChange={onGroup} options={GROUP_OPTIONS} />
            <span className="gf-tb-label">密度</span>
            <Seg<Density> label="密度" value={density} onChange={onDensity} options={DENSITY_OPTIONS} />
          </>
        )}
        {!archive && (
          <span className="gf-mono gf-tb-count">
            {shown}
            {shown !== pool ? ` / ${pool}` : ""} 件
          </span>
        )}
      </div>
    </div>
  );
}
